/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createProductUrl } from '@/lib/product/product-utils';
import { routes, routeHref } from '@/route-paths';

/**
 * Shared model for the `breakout.linkPicker` custom Page Designer editor (UCR-2764). The built-in
 * `type: 'url'` editor's object tabs (Product/Category/Page) can't build a URL on this single-site
 * storefront (`url.prefix = '/'`, no `:siteId` to substitute), so those tabs never Apply. The custom
 * picker sidesteps that by storing a TYPED REFERENCE that the component resolves to a route at render
 * time. See `cartridges/bm_app_storefrontnext_smg/.../editors/breakout/linkPicker.{json,js}`.
 */
export type LinkPickerType = 'product' | 'category' | 'page' | 'url' | 'chat' | 'cookieSettings';

/**
 * Sentinel href a `type: 'chat'` link resolves to (UCR-2926). It is NOT a real destination — the global
 * chat-link interceptor (src/components/sierra-chat/chat-link-interceptor.tsx) catches clicks on anchors
 * with this href and opens the Sierra chatbot instead of navigating. Resolved as `external: true` so
 * adopters render it as a plain non-navigating `<a>` (the site-aware `<Link>` would rewrite the hash).
 */
export const CHAT_LINK_HREF = '#open-chat';

/**
 * Sentinel href a `type: 'cookieSettings'` link resolves to (UCR-2991). Same contract as
 * {@link CHAT_LINK_HREF}: not a real destination, but a marker the global cookie-settings
 * interceptor (src/components/onetrust/cookie-settings-link-interceptor.tsx) turns into a call that
 * reopens the OneTrust preference centre.
 *
 * There is deliberately no `/privacy-choices` route behind this — the preference centre is a modal
 * the OneTrust SDK draws, not a page.
 */
export const COOKIE_SETTINGS_HREF = '#cookie-settings';

/** The value the `linkPicker` editor stores. `value` is an id (product/category/page) or a URL/path. */
export interface LinkPickerValue {
    type: LinkPickerType;
    value: string;
    displayName?: string;
}

/**
 * A link attribute value. Either the new picker object, or a bare string — components migrated from the
 * built-in `type: 'url'` editor (footer link, social link, content card) have existing authored content
 * stored as plain strings, so the resolver must keep rendering those unchanged.
 */
export type LinkValue = string | LinkPickerValue;

/**
 * The custom editor id that a `type: 'custom'` link attribute references.
 *
 * IMPORTANT: `generate-pd-cartridge` reads decorator args by STATIC AST analysis — it only resolves
 * object literals declared in the same file, so a cross-module imported const emits a non-object
 * `editor_definition` and fails schema validation. So DON'T reference this from an `@AttributeDefinition`;
 * inline the literal there: `editorDefinition: { type: 'breakout.linkPicker' }`. This export exists for
 * non-decorator references (tests, docs) only.
 */
export const LINK_PICKER_EDITOR_TYPE = 'breakout.linkPicker';

/** Internal paths go through the site-aware Link; absolute/external URLs (and mailto/tel) use a plain anchor. */
export function isExternalUrl(url: string): boolean {
    return /^(https?:)?\/\//.test(url) || url.startsWith('mailto:') || url.startsWith('tel:');
}

/**
 * Hrefs a picked link cannot build on its own, resolved by the component's loader — see
 * `createLinkPageLoader` in `link-picker.server.ts`. A link has exactly one type, so at most one of
 * these is ever populated.
 */
export interface LinkResolvedHrefs {
    /** `/learn/<folder>/<id>` for a Page link. */
    pageHref?: string | null;
    /**
     * The canonical PDP path for a Product link — `/product/<name-slug>` for a standard product
     * (UCR-3036), `/product/<master>/<sku>` for a variant (UCR-3056).
     */
    productHref?: string | null;
}

/**
 * Resolve a picked link (or a legacy URL string) to a storefront href. Category ids build the SF Next
 * route directly; a URL/legacy string is used as-is; a Page id and a standard product's name slug are
 * both resolved server-side and supplied via `resolved`. Anything unset or unresolved yields a
 * non-navigating `#`.
 *
 * @param resolved the loader-resolved hrefs, or (legacy call form) just the `/learn/…` `pageHref` string.
 */
export function resolveLinkHref(
    link: LinkValue | undefined,
    resolved?: LinkResolvedHrefs | string | null
): { href: string; external: boolean } {
    const { pageHref, productHref } =
        typeof resolved === 'string' || resolved == null ? { pageHref: resolved, productHref: null } : resolved;
    // Legacy string value (authored before the link picker) → treat as a URL/path.
    if (typeof link === 'string') {
        const value = link.trim();
        return value ? { href: value, external: isExternalUrl(value) } : { href: '#', external: false };
    }
    if (!link || !link.value?.trim()) return { href: '#', external: false };
    const value = link.value.trim();
    switch (link.type) {
        case 'product':
            // `productHref` is the canonical PDP URL when the loader resolved one — a master keeps the
            // bare id, which is already canonical. Anything left unresolved links by id and the PDP
            // loader 301s it onto the canonical form (UCR-3036, UCR-3056).
            return { href: productHref || createProductUrl(value), external: false };
        case 'category':
            return { href: routeHref(routes.category, { categoryId: value }), external: false };
        case 'page':
            return { href: pageHref || '#', external: false };
        case 'url':
            return { href: value, external: isExternalUrl(value) };
        case 'chat':
            // Non-navigating: the chat-link interceptor opens Sierra on click. `external: true` forces a
            // plain <a> (the site-aware <Link> would rewrite the hash href). See CHAT_LINK_HREF.
            return { href: CHAT_LINK_HREF, external: true };
        case 'cookieSettings':
            // Non-navigating: the cookie-settings interceptor reopens the OneTrust preference centre.
            // Same `external: true` reasoning as 'chat'. See COOKIE_SETTINGS_HREF.
            return { href: COOKIE_SETTINGS_HREF, external: true };
        default:
            return { href: '#', external: false };
    }
}
